/**
 * Spec 197 — Track B: Admin Impersonation Banner
 *
 * Shown when the current session is an admin impersonation session.
 * Sticky below the main nav, sets --banner-height CSS variable via ResizeObserver
 * so any admin tab bars that are also sticky can offset themselves correctly.
 *
 * Decodes the JWT exp claim to display a live countdown and auto-logout when
 * the impersonation token expires (skeptical review 2026-04-30 hardening).
 *
 * "Exit impersonation" closes the tab (opens in a new window by design).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

// Read JWT exp claim without signature verification — used only for the
// client-side countdown. The token itself is validated server-side on every
// request; this is purely a UX hint so staff don't keep working past expiry.
function getJwtExpSeconds(token: string | null): number | null {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64)) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

function formatRemaining(secondsLeft: number): string {
  const safe = Math.max(0, secondsLeft);
  const mm = Math.floor(safe / 60).toString().padStart(2, '0');
  const ss = Math.floor(safe % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
}

const ImpersonationBanner: React.FC = () => {
  const { impersonatedBy, user, logout } = useAuth();
  const bannerRef = useRef<HTMLDivElement>(null);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  // Tick down once a second based on the JWT exp; logout + close tab at zero.
  useEffect(() => {
    if (!impersonatedBy) return;
    const token = localStorage.getItem('authToken');
    const expSec = getJwtExpSeconds(token);
    if (expSec === null) return;

    const tick = () => {
      const remaining = expSec - Math.floor(Date.now() / 1000);
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        logout();
        window.close();
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [impersonatedBy, logout]);

  // Update --banner-height CSS variable on the document root whenever the
  // banner mounts/unmounts or its height changes (e.g. font scaling, wrapping)
  useEffect(() => {
    const el = bannerRef.current;
    if (!el) {
      document.documentElement.style.setProperty('--banner-height', '0px');
      return;
    }

    const updateHeight = () => {
      document.documentElement.style.setProperty(
        '--banner-height',
        `${el.getBoundingClientRect().height}px`,
      );
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);

    return () => {
      observer.disconnect();
      document.documentElement.style.setProperty('--banner-height', '0px');
    };
  }, []);

  // Only render when this is an impersonation session
  if (!impersonatedBy) return null;

  const handleExit = () => {
    // Impersonation sessions always open in a new tab; closing it
    // returns the admin to the original admin console window.
    window.close();
  };

  return (
    <div
      ref={bannerRef}
      role="alert"
      aria-live="polite"
      className="impersonation-banner sticky z-[99] bg-amber-500 text-white"
      style={{
        top: 'calc(env(safe-area-inset-top, 0px) + var(--nav-height, 56px))',
      }}
    >
      <div className="max-w-[1400px] mx-auto px-3 py-2 sm:px-6 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm font-medium min-w-0">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="shrink-0"
          >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          <span className="truncate">
            Viewing as{' '}
            <strong>
              {user?.displayName || user?.username || user?.name || user?.email || 'user'}
            </strong>
            {' '}&mdash; impersonation session
            {secondsLeft !== null && (
              <>
                {' '}(expires in <span className="tabular-nums">{formatRemaining(secondsLeft)}</span>)
              </>
            )}
          </span>
        </div>

        <button
          type="button"
          onClick={handleExit}
          className="shrink-0 px-3 py-1 text-sm font-semibold rounded border border-white/60 bg-white/10 text-white cursor-pointer transition-colors hover:bg-white/20 hover:border-white focus:outline-none focus:ring-2 focus:ring-white/60"
        >
          Exit impersonation
        </button>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
